import type { Publication } from '~/interfaces/publication';
import type { Subscription } from '~/interfaces/subscriptions';

import { DevScenarioProvider } from './DevScenarioProvider';
import { EffectiveGroupSubscriptionsProvider } from './EffectiveGroupSubscriptionsProvider';
import { IdentitiesProvider } from './IdentitiesProvider';
import { ModalCoordinationProvider } from './ModalCoordinationProvider';
import { PreflightProvider } from './PreflightProvider';
import { TabStateProvider } from './TabStateProvider';

type Props = {
  subscription: Subscription;
  publication: Publication;
  subscriberId: string;
  isLoggedIn: boolean;
  children: React.ReactNode;
};

/**
 * Composition root for the v2 subscription-management surface. Owns the order in
 * which the cross-cutting providers (dev scenario, modal coordination, connected
 * accounts/identities, preflight visibility, tab state) wrap the page so route components only render
 * a single `<SubscriptionManagementProvider>` instead of hand-nesting each one.
 * `IdentitiesProvider` sits above the tab switch so its OAuth / billing-portal
 * redirect handling runs on every tab. `PreflightProvider` sits above `TabStateProvider` so the
 * BE-resolved section-availability flags (and the page's loading gate) resolve before tab state and
 * children render. `EffectiveGroupSubscriptionsProvider` sits
 * below `DevScenarioProvider` and derives the scenario-applied
 * `effectiveGroupSubscriptions` the General tab reads for overlap detection — it
 * fetches its own lightweight read of the groups. The heavier
 * `GroupSubscriptionsProvider` (Groups-tab data, mutators, modals) is mounted
 * locally around the Groups tab in `PageInner`, so it only loads when that tab is open.
 */
export const SubscriptionManagementProvider = ({
  subscription,
  publication,
  subscriberId,
  isLoggedIn,
  children,
}: Props) => (
  <DevScenarioProvider subscription={subscription}>
    <ModalCoordinationProvider>
      <IdentitiesProvider subscription={subscription} subscriberId={subscriberId}>
        <PreflightProvider subscriberId={subscriberId} isLoggedIn={isLoggedIn}>
          <TabStateProvider>
            <EffectiveGroupSubscriptionsProvider publication={publication} isLoggedIn={isLoggedIn}>
              {children}
            </EffectiveGroupSubscriptionsProvider>
          </TabStateProvider>
        </PreflightProvider>
      </IdentitiesProvider>
    </ModalCoordinationProvider>
  </DevScenarioProvider>
);
